import { useMemo, useState } from 'react';
import { useClientPagination } from '@/hooks/useClientPagination';
import { type ColumnDef } from '@tanstack/react-table';
import { ArrowUpRight, ArrowDownRight, ChevronRight, Home, ListOrdered } from 'lucide-react';
import { DataView } from '@/components/data/DataView';
import { ProductDrillDrawer } from '@/components/products/ProductDrillDrawer';
import { useProductPerformance, type ProductDrilldownFilters } from '@/api/bi';
import type {
  InventoryTaxonomyResponse,
  ProductGroupBy,
  ProductPerfRow,
} from '@/api/bi';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { ProductScope } from '@/pages/products/useProductScope';

/**
 * Brand/category/sub-category drill-down matrix.
 *
 * Walks the real taxonomy chain — Category Group ▸ Category ▸ Sub-Category ▸
 * Product — by pushing the clicked node into the shared rail selection, so
 * the *whole page* re-scopes as you drill. The current level is derived from
 * which single-select rail filters are set, which keeps the matrix and rail
 * in perfect lockstep (no second source of truth).
 */
const DRILL_CHAIN: ProductGroupBy[] = ['category_group', 'category', 'subcategory', 'product'];

const LEVEL_LABEL: Record<ProductGroupBy, string> = {
  category_group: 'Category group',
  category: 'Category',
  subcategory: 'Sub-category',
  product: 'Product',
  brand: 'Brand',
};

export interface HierarchyMatrixProps {
  scope: ProductScope;
  taxonomy: InventoryTaxonomyResponse | undefined;
  onProductClick?: (row: ProductPerfRow) => void;
}

export function HierarchyMatrix({ scope, taxonomy, onProductClick }: HierarchyMatrixProps) {
  const level = currentLevel(scope);
  const [drillRow, setDrillRow] = useState<ProductPerfRow | null>(null);

  const query = useProductPerformance({
    start: scope.iso.start,
    end: scope.iso.end,
    compareStart: scope.compareEnabled ? scope.compareRange?.start : undefined,
    compareEnd: scope.compareEnabled ? scope.compareRange?.end : undefined,
    groupBy: level,
    limit: 200,
    ...scope.taxonomy,
  });

  const rows = query.data?.rows ?? [];
  const pagination = useClientPagination(12, `${level}-${rows.length}-${scope.iso.start}`);

  const crumbs = useMemo(() => buildCrumbs(scope, taxonomy), [scope, taxonomy]);

  const drillFilters = useMemo(
    () => (drillRow ? drillFiltersForRow(scope, drillRow, level) : null),
    [drillRow, scope, level],
  );

  const columns = useMemo<ColumnDef<ProductPerfRow, unknown>[]>(
    () => [
      {
        id: 'name',
        header: LEVEL_LABEL[level],
        accessorFn: (r) => r.name,
        cell: ({ row }) => (
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm font-medium">{row.original.name}</span>
            {level !== 'product' ? (
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" aria-hidden />
            ) : null}
          </div>
        ),
      },
      {
        id: 'gmv',
        header: () => <div className="text-right">GMV</div>,
        accessorFn: (r) => r.gmv,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm font-medium">{fmt.npr(row.original.gmv)}</div>
        ),
      },
      {
        id: 'contribution',
        header: () => <div className="text-right">Share</div>,
        accessorFn: (r) => r.contribution_pct,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm">{fmt.pct(row.original.contribution_pct)}</div>
        ),
      },
      {
        id: 'units',
        header: () => <div className="text-right">Units</div>,
        accessorFn: (r) => r.units,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm">{fmt.number(row.original.units)}</div>
        ),
      },
      {
        id: 'orders',
        header: () => <div className="text-right">Orders</div>,
        accessorFn: (r) => r.orders,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm">{fmt.number(row.original.orders)}</div>
        ),
      },
      {
        id: 'margin',
        header: () => <div className="text-right">Margin %</div>,
        accessorFn: (r) => r.margin_pct ?? -1,
        cell: ({ row }) => {
          const v = row.original.margin_pct;
          if (v == null) return <div className="text-right text-xs text-muted-foreground">—</div>;
          return <div className="kpi-num text-right text-sm">{fmt.pct(v)}</div>;
        },
      },
      {
        id: 'delta',
        header: () => <div className="text-right">Δ GMV</div>,
        accessorFn: (r) => r.gmv_delta_pct ?? 0,
        cell: ({ row }) => <DeltaCell pct={row.original.gmv_delta_pct} />,
      },
      {
        id: 'drill',
        header: '',
        cell: ({ row }) => (
          <button
            type="button"
            title="View orders & customers"
            className="press inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-ring"
            onClick={(e) => {
              e.stopPropagation();
              setDrillRow(row.original);
            }}
          >
            <ListOrdered className="h-3.5 w-3.5" aria-hidden />
          </button>
        ),
      },
    ],
    [level],
  );

  const handleRowClick = (row: ProductPerfRow) => {
    if (level === 'category_group') scope.setCategoryGroupIds([row.id]);
    else if (level === 'category') scope.setCategoryIds([row.id]);
    else if (level === 'subcategory') scope.setSubcategoryIds([row.id]);
    else onProductClick?.(row);
  };

  return (
    <div className="space-y-2">
      <Breadcrumbs crumbs={crumbs} />
      <DataView<ProductPerfRow>
        title="Hierarchy performance"
        description={`Drill ${LEVEL_LABEL[level].toLowerCase()} → ${
          level === 'product' ? 'detail' : LEVEL_LABEL[DRILL_CHAIN[DRILL_CHAIN.indexOf(level) + 1]].toLowerCase()
        }. Click a row to dive in, or use the list icon for orders & customers.`}
        data={rows}
        columns={columns}
        isLoading={query.isLoading}
        isError={query.isError}
        onRetry={() => void query.refetch()}
        onRowClick={handleRowClick}
        clientPagination
        pagination={{
          ...pagination,
          total: rows.length,
        }}
        emptyTitle="No sales in this scope"
        density="compact"
      />
      {drillFilters && drillRow ? (
        <ProductDrillDrawer
          open={Boolean(drillRow)}
          onOpenChange={(open) => { if (!open) setDrillRow(null); }}
          title={`Orders & customers · ${drillRow.name}`}
          returnPath="/products"
          filters={drillFilters}
        />
      ) : null}
    </div>
  );
}

function drillFiltersForRow(
  scope: ProductScope,
  row: ProductPerfRow,
  level: ProductGroupBy,
): ProductDrilldownFilters {
  const base: ProductDrilldownFilters = {
    start: scope.iso.start,
    end: scope.iso.end,
    storeId: scope.storeId,
    storeIds: scope.storeIds,
    vendorIds: scope.vendorIds,
    brandIds: scope.brandIds,
    categoryGroupIds: scope.categoryGroupIds,
    categoryIds: scope.categoryIds,
    subcategoryIds: scope.subcategoryIds,
    q: scope.productQuery.trim() || undefined,
  };
  if (level === 'category_group') return { ...base, categoryGroupIds: [row.id] };
  if (level === 'category') return { ...base, categoryIds: [row.id] };
  if (level === 'subcategory') return { ...base, subcategoryIds: [row.id] };
  return { ...base, productIds: [row.id] };
}

function currentLevel(scope: ProductScope): ProductGroupBy {
  if (scope.subcategoryIds.length === 1) return 'product';
  if (scope.categoryIds.length === 1) return 'subcategory';
  if (scope.categoryGroupIds.length === 1) return 'category';
  return 'category_group';
}

interface Crumb {
  label: string;
  onClick: () => void;
  active: boolean;
}

function buildCrumbs(scope: ProductScope, taxonomy: InventoryTaxonomyResponse | undefined): Crumb[] {
  const crumbs: Crumb[] = [
    {
      label: 'All products',
      active: scope.categoryGroupIds.length === 0,
      onClick: () => {
        scope.setCategoryGroupIds([]);
        scope.setCategoryIds([]);
        scope.setSubcategoryIds([]);
      },
    },
  ];
  if (scope.categoryGroupIds.length === 1) {
    const id = scope.categoryGroupIds[0];
    const name = taxonomy?.category_groups.find((g) => g.id === id)?.name ?? `Group #${id}`;
    crumbs.push({
      label: name,
      active: scope.categoryIds.length === 0,
      onClick: () => {
        scope.setCategoryIds([]);
        scope.setSubcategoryIds([]);
      },
    });
  }
  if (scope.categoryIds.length === 1) {
    const id = scope.categoryIds[0];
    const name = taxonomy?.categories.find((c) => c.id === id)?.name ?? `Category #${id}`;
    crumbs.push({
      label: name,
      active: scope.subcategoryIds.length === 0,
      onClick: () => scope.setSubcategoryIds([]),
    });
  }
  if (scope.subcategoryIds.length === 1) {
    const id = scope.subcategoryIds[0];
    const name = taxonomy?.subcategories.find((s) => s.id === id)?.name ?? `Sub #${id}`;
    crumbs.push({ label: name, active: true, onClick: () => {} });
  }
  return crumbs;
}

function Breadcrumbs({ crumbs }: { crumbs: Crumb[] }) {
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs">
      {crumbs.map((c, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 ? <ChevronRight className="h-3 w-3 text-muted-foreground/40" aria-hidden /> : null}
          <button
            type="button"
            onClick={c.onClick}
            disabled={c.active}
            className={cn(
              'press inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 focus-ring',
              c.active
                ? 'font-medium text-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {i === 0 ? <Home className="h-3 w-3" aria-hidden /> : null}
            {c.label}
          </button>
        </span>
      ))}
    </div>
  );
}

function DeltaCell({ pct }: { pct: number | null }) {
  if (pct == null) return <div className="text-right text-xs text-muted-foreground">—</div>;
  const positive = pct > 0;
  return (
    <div
      className={cn(
        'inline-flex w-full items-center justify-end gap-0.5 text-xs font-medium',
        positive ? 'text-success' : pct < 0 ? 'text-danger' : 'text-muted-foreground',
      )}
    >
      {positive ? <ArrowUpRight className="h-3 w-3" /> : pct < 0 ? <ArrowDownRight className="h-3 w-3" /> : null}
      {fmt.delta(pct, { unit: 'pct' })}
    </div>
  );
}
